import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Dashboard from "@/components/Dashboard";
import CreditScanner from "@/components/CreditScanner";
import TradeMasterChat from "@/components/TradeMasterChat";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import DemoPage from "@/pages/DemoPage";
import TermsOfService from "@/pages/TermsOfService";
import LegalCenter from "@/pages/legal/LegalCenter";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import SafetyPolicy from "@/pages/legal/SafetyPolicy";
import CodeOfConduct from "@/pages/legal/CodeOfConduct";
import AcceptableUse from "@/pages/legal/AcceptableUse";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import AdminMessages from "@/pages/AdminMessages";
import AdminConsole from "@/pages/AdminConsole";
import ChartAnalysis from "@/pages/ChartAnalysis";
import StockStrategyAnalyzer from "@/pages/StockStrategyAnalyzer";
import MentorPage from "@/pages/MentorPage";
import AuthCallback from "@/components/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SubscriptionProvider, useSubscription } from "@/contexts/SubscriptionContext";
import { AIModelProvider } from "@/contexts/AIModelContext";
import { PayoffDiagramProvider } from "@/contexts/PayoffDiagramContext";
import { Toaster } from "@/components/ui/sonner";
import DisclaimerModal from "@/components/DisclaimerModal";
import RouteSeo from "@/components/RouteSeo";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import InstallHelpBanner from "@/components/InstallHelpBanner";
import PremiumGate from "@/components/PremiumGate";
import { GlobalChartModal, FloatingChartButton } from "@/components/GlobalChartModal";
import { Loader2 } from "lucide-react";

// Register service worker for PWA
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('SW registered:', registration.scope);
        })
        .catch((error) => {
          console.log('SW registration failed:', error);
        });
    });
  }
};

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-900">
    <Loader2 className="h-10 w-10 text-amber-400 animate-spin" />
  </div>
);

// Requires an authenticated Aloha account. Optionally requires an active subscription/trial.
// Guests see the public landing page; signed-in members land on the dashboard.
const HomeRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  return isAuthenticated ? <Dashboard /> : <LandingPage />;
};

const Protected = ({ children, premium = false, featureName = 'This feature' }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (premium) return <PremiumRouteGuard featureName={featureName}>{children}</PremiumRouteGuard>;
  return children;
};

const PremiumRouteGuard = ({ children, featureName }) => {
  const { isPremium, loading } = useSubscription();
  if (loading) return <FullScreenLoader />;
  if (!isPremium) return <PremiumGate featureName={featureName} />;
  return children;
};

// App-wide premium widgets (chat, chart) only render for entitled users
const PremiumWidgets = () => {
  const { isAuthenticated } = useAuth();
  const { isPremium } = useSubscription();
  const [chartModalOpen, setChartModalOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k' && isPremium) {
        e.preventDefault();
        setChartModalOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPremium]);

  if (!isAuthenticated || !isPremium) return null;

  return (
    <>
      <GlobalChartModal isOpen={chartModalOpen} onClose={() => setChartModalOpen(false)} />
      <FloatingChartButton onOpen={() => setChartModalOpen(true)} />
      <TradeMasterChat />
    </>
  );
};

// Layout wrapper to conditionally show components
const LEGAL_PATHS = ['/terms', '/legal', '/privacy', '/refund-policy', '/safety', '/code-of-conduct', '/acceptable-use', '/about', '/contact'];

const AppLayout = ({ children }) => {
  const location = useLocation();
  const hideChrome = LEGAL_PATHS.includes(location.pathname);

  return (
    <>
      <RouteSeo />
      {!hideChrome && <InstallHelpBanner />}
      {!hideChrome && <DisclaimerModal />}

      {children}

      {/* Premium-only floating widgets */}
      <PremiumWidgets />

      {!hideChrome && <PWAInstallPrompt />}
      {!hideChrome && <DisclaimerFooter />}
    </>
  );
};

function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <div className="App app-shell">
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <AIModelProvider>
            <PayoffDiagramProvider>
              <AppLayout>
                <Routes>
                  <Route path="/" element={<HomeRoute />} />
                  <Route path="/picks" element={<Dashboard />} />
                  <Route path="/demo" element={<DemoPage />} />
                  <Route path="/credit-scanner" element={<Protected premium featureName="Credit Scanner"><CreditScanner /></Protected>} />
                  <Route path="/chart-analysis" element={<Protected premium featureName="Chart Analysis"><ChartAnalysis /></Protected>} />
                  <Route path="/stock-analyzer" element={<Protected premium featureName="Stock Strategy Analyzer"><StockStrategyAnalyzer /></Protected>} />
                  <Route path="/mentor" element={<Protected premium featureName="Ruthless Mentor"><MentorPage /></Protected>} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/legal" element={<LegalCenter />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/safety" element={<SafetyPolicy />} />
                  <Route path="/code-of-conduct" element={<CodeOfConduct />} />
                  <Route path="/acceptable-use" element={<AcceptableUse />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/admin" element={<AdminConsole />} />
                  <Route path="/admin/messages" element={<AdminMessages />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                </Routes>
              </AppLayout>
            </PayoffDiagramProvider>
            </AIModelProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
